import { Graphics, Sprite, type Container, type Texture } from 'pixi.js';
import { hasLineOfSight } from '@prism/sim';

import { TILE_PX } from './constants.js';

/** Whether a tile stops a shot. The renderer derives this from the build mask. */
export type BlockedFn = (tileX: number, tileY: number) => boolean;

/**
 * The tiles a tower at (tileX, tileY) with `range` can both reach and SEE.
 *
 * Returns null when nothing is blocked — the common case, and the one worth keeping free
 * of a stencil pass entirely. Pure and Pixi-free so the geometry can be tested directly;
 * the drawing on top of it is the part that needs eyes.
 */
export function visibleTiles(
  tileX: number,
  tileY: number,
  range: number,
  blocked: BlockedFn,
): { x: number; y: number }[] | null {
  const cx = tileX + 0.5;
  const cy = tileY + 0.5;
  const tiles: { x: number; y: number }[] = [];
  let clipped = false;

  // A ring wider than the circle, because a tile whose nearest point is exactly `range`
  // away still counts and `floor` drops it when cx - range lands on an integer. The
  // distance test below does the real work; the extra ring is a handful of rejections.
  for (let y = Math.floor(cy - range) - 1; y <= Math.floor(cy + range) + 1; y++) {
    for (let x = Math.floor(cx - range) - 1; x <= Math.floor(cx + range) + 1; x++) {
      // Distance to the NEAREST point of the tile, not its centre: a tile the circle only
      // clips at one corner still shows that corner, and the mask must not erase it.
      // Over-covering is free — the mask is intersected with the circle sprite anyway.
      const dx = cx < x ? x - cx : cx > x + 1 ? cx - (x + 1) : 0;
      const dy = cy < y ? y - cy : cy > y + 1 ? cy - (y + 1) : 0;
      if (dx * dx + dy * dy > range * range) continue;

      // A blocker is carved out explicitly. `hasLineOfSight` exempts its endpoints — it
      // has to, or a tower on a tile the mask calls solid would be blind for a whole run
      // — so a wall next door would otherwise paint itself as covered.
      if (blocked(x, y) || !hasLineOfSight(blocked, cx, cy, x + 0.5, y + 0.5)) {
        clipped = true;
        continue;
      }
      tiles.push({ x, y });
    }
  }

  return clipped ? tiles : null;
}

/**
 * One range indicator, clipped to what the tower can actually SEE (R-03-22, R-03-24).
 *
 * Blockers stop shots, so a plain circle over-promises: it paints coverage behind a rock
 * that the tower can never fire into. The bright fill is masked to the tiles that pass
 * the sim's OWN `hasLineOfSight` — imported rather than reimplemented, because a renderer
 * that re-derives a rule is a renderer that will eventually disagree with the simulation
 * the server replays.
 *
 * A bare outline of the full circle stays behind it — no fill, so the covered ground is
 * the only lit ground. Without that outline a blocked tower just looks like a tower with
 * less range, and the whole point is to show that the terrain is what took the bite.
 */
export class RangeCircle {
  private readonly shadow: Sprite;
  private readonly bright: Sprite;
  /** Built lazily: an unobstructed circle needs no stencil pass at all. */
  private mask: Graphics | null = null;
  /** Last (tile, range, map) the mask was traced for — hover re-sends this every frame. */
  private tracedFor = '';
  private tracedBlocked: BlockedFn | null = null;

  constructor(
    fillTexture: Texture,
    ringTexture: Texture,
    private readonly layer: Container,
  ) {
    // Both created up front and in this order so the shadow is always behind the bright
    // fill; inserting one later would mean juggling child indices on every hover.
    this.shadow = new Sprite(ringTexture);
    this.shadow.anchor.set(0.5, 0.5);
    this.shadow.visible = false;
    this.bright = new Sprite(fillTexture);
    this.bright.anchor.set(0.5, 0.5);
    this.bright.visible = false;
    this.layer.addChild(this.shadow, this.bright);
  }

  hide(): void {
    this.bright.visible = false;
    this.shadow.visible = false;
  }

  show(tileX: number, tileY: number, range: number, blocked: BlockedFn | null): void {
    const cx = tileX * TILE_PX + TILE_PX / 2;
    const cy = tileY * TILE_PX + TILE_PX / 2;
    const diameter = range * TILE_PX * 2;
    for (const s of [this.shadow, this.bright]) {
      s.width = diameter;
      s.height = diameter;
      s.position.set(cx, cy);
    }
    this.bright.visible = true;

    // Tracing walks a line per tile in the bounding box. Nothing about the answer changes
    // while the circle sits still, and `setFocusRange` fires on every frame of a hover.
    const key = `${tileX},${tileY},${range}`;
    if (key === this.tracedFor && blocked === this.tracedBlocked) {
      this.shadow.visible = this.mask !== null;
      return;
    }
    this.tracedFor = key;
    this.tracedBlocked = blocked;

    if (blocked === null || !this.trace(tileX, tileY, range, blocked)) {
      this.bright.mask = null;
      if (this.mask) {
        this.mask.destroy();
        this.mask = null;
      }
      this.shadow.visible = false;
      return;
    }
    this.shadow.visible = true;
  }

  /**
   * Redraw the visible-tile mask. Returns false when the circle is unobstructed.
   */
  private trace(tileX: number, tileY: number, range: number, blocked: BlockedFn): boolean {
    const tiles = visibleTiles(tileX, tileY, range, blocked);
    if (!tiles) return false;

    const g = this.mask ?? new Graphics();
    g.clear();
    for (const t of tiles) g.rect(t.x * TILE_PX, t.y * TILE_PX, TILE_PX, TILE_PX);
    g.fill({ color: 0xffffff });

    if (!this.mask) {
      this.mask = g;
      // A Pixi mask has to be in the display list to be traversed; it draws nothing.
      this.layer.addChild(g);
      this.bright.mask = g;
    }
    return true;
  }
}
